import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const NumberRange = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('number_ranges');
  const [viewItem, setViewItem] = useState(null);
  const [numberRanges, setNumberRanges] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRange, setEditingRange] = useState(null);
  const [selectedRangeType, setSelectedRangeType] = useState('INTERNAL');

  useEffect(() => {
    loadNumberRanges();
  }, []);

  const loadNumberRanges = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/number-ranges`);
      setNumberRanges(response.data || []);
    } catch (error) {
      console.error('Error loading number ranges:', error);
      toast.error('Failed to load number ranges');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (range = null) => {
    setEditingRange(range);
    if (range) {
      setSelectedRangeType(range.range_type);
    } else {
      setSelectedRangeType('INTERNAL');
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const prefixValue = (formData.get('prefix') || '').toUpperCase().trim();
    
    const data = {
      number_range_id: (formData.get('number_range_id') || '').toUpperCase(),
      description: formData.get('description'),
      range_type: selectedRangeType,
      prefix: prefixValue || null
    };

    // For internal ranges, add from/to numbers
    if (selectedRangeType === 'INTERNAL') {
      data.from_number = parseInt(formData.get('from_number'));
      data.to_number = parseInt(formData.get('to_number'));
      
      // Validation
      if (data.from_number >= data.to_number) {
        toast.error('From Number must be less than To Number');
        return;
      }
    } else {
      // External range: prefix is mandatory and must be exactly 3 characters
      if (!prefixValue) {
        toast.error('Prefix is mandatory for External number ranges');
        return;
      }
      if (prefixValue.length !== 3) {
        toast.error('Prefix must be exactly 3 characters for External number ranges');
        return;
      }
      data.prefix = prefixValue;
      data.from_number = null;
      data.to_number = null;
    }

    try {
      if (editingRange) {
        await axios.put(`${BACKEND_URL}/api/number-ranges/${editingRange.number_range_id}`, {
          description: data.description
        });
        toast.success('Number Range updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/number-ranges`, data);
        toast.success('Number Range created successfully');
      }
      setIsDialogOpen(false);
      loadNumberRanges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save number range');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Number Ranges</h1>
            <p className="text-sm text-gray-500 mt-1">Manage number range configurations for Material Master</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Number Ranges</h2>
            {writable && (<Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Number Range
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : numberRanges.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No number ranges found. Create one to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Range ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prefix</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Range</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {numberRanges.map((range) => (
                    <tr key={range.number_range_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{range.number_range_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{range.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 text-xs rounded-full ${range.range_type === 'INTERNAL' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                          {range.range_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{range.prefix || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {range.range_type === 'INTERNAL' ? `${range.from_number} - ${range.to_number}` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {range.range_type === 'INTERNAL' ? range.current_number : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <RecordActions doctype="number_ranges" onView={() => setViewItem(range)} onEdit={() => handleOpenDialog(range)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingRange ? 'Edit Number Range' : 'Create Number Range'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="number_range_id">Number Range ID * (10 characters)</Label>
                  <Input
                    id="number_range_id"
                    name="number_range_id"
                    defaultValue={editingRange?.number_range_id || ''}
                    disabled={editingRange !== null}
                    required
                    maxLength={10}
                    className="mt-1 uppercase"
                    placeholder="e.g., MAT01, MAT02"
                  />
                </div>
                <div>
                  <Label htmlFor="range_type">Range Type *</Label>
                  <Select value={selectedRangeType} onValueChange={setSelectedRangeType} disabled={editingRange !== null}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INTERNAL">Internal (System Generated)</SelectItem>
                      <SelectItem value="EXTERNAL">External (User Provided)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  name="description"
                  defaultValue={editingRange?.description || ''}
                  required
                  maxLength={100}
                  className="mt-1"
                  placeholder="e.g., Raw Materials Number Range"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="prefix">
                    Prefix {selectedRangeType === 'EXTERNAL' ? '* (Exactly 3 characters)' : '(Optional)'}
                  </Label>
                  <Input
                    id="prefix"
                    name="prefix"
                    defaultValue={editingRange?.prefix || ''}
                    disabled={editingRange !== null}
                    maxLength={selectedRangeType === 'EXTERNAL' ? 3 : 10}
                    minLength={selectedRangeType === 'EXTERNAL' ? 3 : 0}
                    required={selectedRangeType === 'EXTERNAL'}
                    className="mt-1 uppercase"
                    placeholder={selectedRangeType === 'EXTERNAL' ? 'e.g., EXT, ABC' : 'e.g., MAT, RM'}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedRangeType === 'EXTERNAL' 
                      ? 'Mandatory 3-character prefix for external material numbers' 
                      : 'Optional prefix for material numbers'}
                  </p>
                </div>
              </div>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-sm text-orange-800">
                  <strong>Note:</strong> Material numbers will be generated with a standard length of <strong>10 digits</strong>
                  {selectedRangeType === 'INTERNAL' && ' (including any prefix)'}
                </p>
              </div>

              {selectedRangeType === 'INTERNAL' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="from_number">From Number *</Label>
                    <Input
                      id="from_number"
                      name="from_number"
                      type="number"
                      defaultValue={editingRange?.from_number || 1}
                      disabled={editingRange !== null}
                      required={selectedRangeType === 'INTERNAL'}
                      min={0}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="to_number">To Number *</Label>
                    <Input
                      id="to_number"
                      name="to_number"
                      type="number"
                      defaultValue={editingRange?.to_number || 999999}
                      disabled={editingRange !== null}
                      required={selectedRangeType === 'INTERNAL'}
                      min={0}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              {selectedRangeType === 'INTERNAL' && (
                <div className="bg-orange-50 p-3 rounded-md">
                  <p className="text-sm text-orange-800">
                    <strong>Internal Range:</strong> Material numbers will be automatically generated by the system sequentially within the specified From/To range.
                  </p>
                </div>
              )}

              {selectedRangeType === 'EXTERNAL' && (
                <div className="bg-green-50 p-3 rounded-md">
                  <p className="text-sm text-green-800">
                    <strong>External Range:</strong> Users must provide material numbers starting with the mandatory 3-character prefix. 
                    For example, if prefix is "EXT", valid material numbers would be "EXT001", "EXT002", etc.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingRange ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default NumberRange;
